/**
 * The pills the table and the detail modal share.
 *
 * Same shape as pages/Hipaa/components/Badges.tsx: one PILL class, one colour map per
 * enum, so an unhandled value is a type error rather than an unstyled pill.
 */
import { AlertTriangle, Clock, HelpCircle } from 'lucide-react';
import {
  POSTHOG_OUTCOME_LABELS,
  REVENUECAT_OUTCOME_LABELS,
  STATUS_LABELS,
  type DeletionSource,
  type DeletionStatus,
  type PostHogAudit,
  type PostHogOutcome,
  type RevenueCatAudit,
  type RevenueCatOutcome,
} from '../types/accountDeletionTypes';
import { ATTENTION_ATTEMPTS } from '../utils/deletionFilters';

const PILL = 'inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full';

const STATUS_CLASSES: Record<DeletionStatus, string> = {
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
};

export const DeletionStatusPill = ({ status }: { status: DeletionStatus }) => (
  <span className={`${PILL} ${STATUS_CLASSES[status]}`}>{STATUS_LABELS[status]}</span>
);

const SOURCE_CLASSES: Record<DeletionSource, string> = {
  user: 'bg-slate-100 text-slate-600',
  backfill: 'bg-purple-100 text-purple-700',
};

const SOURCE_TITLES: Record<DeletionSource, string> = {
  user: 'The Auth delete trigger fired for this user',
  backfill: 'A tools/ script queued this already-deleted uid',
};

export const SourceBadge = ({ source }: { source: DeletionSource }) => (
  <span className={`${PILL} ${SOURCE_CLASSES[source]}`} title={SOURCE_TITLES[source]}>
    {source}
  </span>
);

const OUTCOME_CLASSES: Record<RevenueCatOutcome, string> = {
  anonymized: 'bg-green-100 text-green-700',
  'not-found': 'bg-gray-100 text-gray-600',
  failed: 'bg-red-100 text-red-700',
};

/**
 * `null` is not "nothing happened" -- RevenueCat runs before the Firestore sweep, so a
 * null here means the job never got past step 1. It reads as amber, not as neutral.
 */
export const RevenueCatOutcomeBadge = ({ audit }: { audit: RevenueCatAudit | null }) => {
  if (audit === null) {
    return (
      <span
        className={`${PILL} bg-amber-100 text-amber-800`}
        title="Step 1 never reported. RevenueCat runs before the Firestore sweep, so nothing after this ran either."
      >
        Not reached
      </span>
    );
  }

  return (
    <span
      className={`${PILL} ${OUTCOME_CLASSES[audit.outcome]}`}
      title={
        audit.errorCode
          ? `Error code: ${audit.errorCode}`
          : `${audit.attributesCleared} attribute(s) cleared`
      }
    >
      {REVENUECAT_OUTCOME_LABELS[audit.outcome]}
    </span>
  );
};

const POSTHOG_OUTCOME_CLASSES: Record<PostHogOutcome, string> = {
  queued: 'bg-green-100 text-green-700',
  'not-found': 'bg-gray-100 text-gray-600',
  failed: 'bg-red-100 text-red-700',
};

/**
 * null on a completed record means the job finished before the PostHog step existed.
 * null on an open record means the job never got past step 2, which reads as amber.
 */
export const PostHogOutcomeBadge = ({
  audit,
  completed,
}: {
  audit: PostHogAudit | null;
  completed: boolean;
}) => {
  if (audit === null) {
    return completed ? (
      <span
        className={`${PILL} bg-gray-100 text-gray-600`}
        title="Completed before the PostHog step existed"
      >
        Not recorded
      </span>
    ) : (
      <span
        className={`${PILL} bg-amber-100 text-amber-800`}
        title="Step 2 never reported. PostHog runs before the Firestore sweep, so nothing after this ran either."
      >
        Not reached
      </span>
    );
  }

  return (
    <span
      className={`${PILL} ${POSTHOG_OUTCOME_CLASSES[audit.outcome]}`}
      title={audit.errorCode ? `Error code: ${audit.errorCode}` : undefined}
    >
      {POSTHOG_OUTCOME_LABELS[audit.outcome]}
    </span>
  );
};

export const AttentionBadge = () => (
  <span
    className={`${PILL} bg-red-100 text-red-700`}
    title={`Failed ${ATTENTION_ATTEMPTS} or more times. The reconciler is not going to fix this on its own.`}
  >
    <AlertTriangle size={11} />
    Attention
  </span>
);

export const StalledBadge = () => (
  <span
    className={`${PILL} bg-amber-100 text-amber-800`}
    title="Unfinished and untouched long enough that an hourly reconciler run has already missed it"
  >
    <Clock size={11} />
    Stalled
  </span>
);

export const InconsistentBadge = () => (
  <span
    className={`${PILL} bg-fuchsia-100 text-fuchsia-700`}
    title="Recorded as completed while still holding a failure. The audit trail is wrong for this record — investigate before trusting deletion evidence."
  >
    <HelpCircle size={11} />
    Inconsistent
  </span>
);
